import { css, useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import {
  Button,
  Listbox,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react';
import type { LocaleType } from '@landsbankinn/l16n';
import {
  Box,
  Flex,
  hexToRgba,
  InputHost,
  Span,
  StyledInput,
  type StyledTheme,
  UnorderedList,
} from '@landsbankinn/ui-kit';
import React, { useEffect, useRef, useState } from 'react';

const RefinedLanguageIcon28: React.FC<{
  color?: 'green main' | 'white font';
}> = ({ color }) => {
  const theme = useTheme() as StyledTheme;
  const stroke =
    color === 'green main' ? theme.colors.green.main : theme.colors.white.font;

  return (
    <svg width="26" height="25" fill="none" viewBox="0 0 26 25" id="globe">
      <circle cx="13" cy="12.5" r="12" stroke={stroke}></circle>
      <path
        stroke={stroke}
        d="M17 12.5c0 3.41-.498 6.475-1.289 8.67-.395 1.099-.855 1.953-1.34 2.524-.487.572-.952.806-1.371.806-.419 0-.884-.234-1.37-.806-.486-.57-.946-1.425-1.341-2.524C9.498 18.974 9 15.91 9 12.5s.498-6.475 1.289-8.67c.395-1.099.855-1.953 1.34-2.524C12.116.734 12.581.5 13 .5c.419 0 .884.234 1.37.806.486.57.946 1.425 1.341 2.525C16.502 6.025 17 9.09 17 12.5z"
      ></path>
      <path
        stroke={stroke}
        strokeLinecap="square"
        d="M2.275 8.5h21.45m-21.45 8h21.45"
      ></path>
    </svg>
  );
};

const StyledInputHost = styled(InputHost)<{ isOpen: boolean }>`
  background: ${({ theme, isOpen }) =>
    !isOpen ? 'transparent' : theme.colors.white.background};
  box-shadow: none;
  border-top-left-radius: 0.2rem;
  border-top-right-radius: 0.2rem;
`;

const StyledSelect = styled(StyledInput)`
  flex-direction: row;
  display: flex;
  align-content: space-between;
  align-items: center;

  &[disabled] svg {
    opacity: 0.5;
  }

  &:focus-visible {
    outline: 0.4rem solid ${({ theme }) => theme.colors.input.focusOutline};
    outline-offset: -0.4rem;
    border-radius: 0.4rem;
    isolation: isolate;
  }
`.withComponent('button');

const Placeholder = styled.span`
  flex-grow: 1;
  padding-right: 2rem;
  font-weight: 500;
`;

const StyledUnorderedList = styled(UnorderedList)<{ isOpen: boolean }>`
  overflow-y: auto;
  overscroll-behavior: contain;
  /* position: relative activates z-index, so the dropdown renders above the absolutely-positioned page content area */
  position: relative;
  z-index: ${({ theme }) => {
    return theme.zIndexConfig.dropdown;
  }};
  cursor: default;
  box-sizing: content-box;
  &:focus {
    outline: none;
  }

  ${({ isOpen }) =>
    isOpen
      ? css`
          @keyframes transformY {
            from {
              transform: translateY(-5px);
              opacity: 0;
            }
            to {
              transform: translateY(0px);
              opacity: 1;
            }
          }
          animation: transformY 0.2s ease-in-out forwards;
        `
      : ''}
`;

const StyledBox = styled(Box)<{ active?: boolean }>`
  .animatedLine {
    animation-name: out;
    animation-fill-mode: reverse;
    animation-duration: 0.3s;
  }

  :hover {
    .animatedLine {
      animation-name: in;
      animation-fill-mode: forwards;
      animation-duration: 0.3s;
    }
  }

  .animatedLine {
    @keyframes in {
      0% {
        transform: translateX(-100%);
        opacity: 0;
      }
      100% {
        transform: translateX(0%);
        opacity: 1;
      }
    }
    @keyframes out {
      0% {
        transform: translateX(0%);
        opacity: 1;
      }
      100% {
        transform: translateX(-100%);
        opacity: 0;
      }
    }
  }

  outline: ${({ active, theme }) =>
    active && theme.isNavigatingByKeyboard
      ? `0.4rem solid ${theme.colors.input.focusOutline}`
      : 'none'};
  outline-offset: ${({ active, theme }) =>
    active && theme.isNavigatingByKeyboard ? '-0.4rem' : '0'};
  border-radius: ${({ active, theme }) =>
    active && theme.isNavigatingByKeyboard ? '0.4rem' : '0'};
  isolation: isolate;
`;

const UnderlineHost = styled('div')`
  position: absolute;
  left: 0rem;
  bottom: 0rem;
  height: 0.1rem;
  background-color: ${({ theme }) =>
    hexToRgba({ hex: theme.colors.blue.font, alpha: 0.1 })};
  overflow: hidden;
  width: 2.8rem;
`;

const StyledTextLink = styled(Span)`
  text-decoration: none;
  list-style-type: none;
  cursor: pointer;
`;

const AnimatedLine = styled('div')`
  background: ${({ theme }) => {
    return theme.colors.blue.font;
  }};
  width: 100%;
  height: 100%;
  opacity: 0;
  transform: translateX(0%);
`;

const labelMap: Record<LocaleType, string> = {
  is: 'Íslenska',
  en: 'English',
  pl: 'Polski',
};

export type LanguageSwitcherProps = {
  items: LocaleType[];
  placeholder?: string;
  value?: LocaleType;
  onSelect?: (value: LocaleType) => void;
  ariaLabel: string;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'children' | 'onSelect'>;

/**
 * Language switcher implemented as ListBox from headlessUI.
 * https://headlessui.com/react/listbox
 */
export function LanguageSwitcher({
  placeholder,
  items,
  value,
  disabled = false,
  onSelect,
  ariaLabel,
}: LanguageSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  let timer: ReturnType<typeof setTimeout>;

  const handleClickOutside = (e: MouseEvent) => {
    if (hostRef.current?.contains(e.target as Element)) {
      return;
    }
    setIsOpen(false);
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (selectedValue: LocaleType) => {
    setIsOpen(false);
    onSelect?.(selectedValue);
  };

  return (
    <Listbox
      value={value}
      onChange={handleSelect}
      as={'div'}
      onMouseEnter={() => {
        if (!disabled) {
          clearTimeout(timer);
          setIsOpen(true);
        }
      }}
      onMouseLeave={() => {
        timer = setTimeout(() => {
          setIsOpen(false);
        }, 500);
      }}
      style={{
        width: (inputRef.current as Element)?.scrollWidth,
        height: (inputRef.current as Element)?.scrollHeight,
      }}
      ref={hostRef}
      disabled={disabled}
    >
      {({ open }: { open: boolean }) => (
        <>
          <StyledInputHost
            ref={inputRef}
            width="170"
            height="54"
            isOpen={isOpen || open}
          >
            <Button
              aria-label={ariaLabel}
              data-testid="language-switcher-button"
              as={StyledSelect}
              paddingLeft="28"
              color="blue font"
            >
              <>
                <Placeholder>{isOpen || open ? placeholder : ''}</Placeholder>
                <RefinedLanguageIcon28
                  color={isOpen || open ? 'green main' : 'white font'}
                />
              </>
            </Button>
          </StyledInputHost>
          {!disabled && !!(open || isOpen) && (
            <ListboxOptions
              static
              as={StyledUnorderedList}
              aria-label={ariaLabel}
              style={{
                boxShadow: 'rgba(149, 157, 165, 0.2) 0px 8px 24px',
              }}
              isOpen={isOpen || open}
            >
              {items.map((item) => (
                <ListboxOption
                  key={item}
                  value={item}
                  as={StyledTextLink}
                  style={{
                    width: (inputRef.current as Element)?.scrollWidth,
                  }}
                >
                  {({ active }: { active: boolean }) => (
                    <Flex
                      mainAxisAlignment="start"
                      background="blue baseLight"
                      paddingVertical="14"
                      paddingHorizontal="28"
                      width="100%"
                    >
                      <StyledBox
                        position="relative"
                        height="30"
                        width="100%"
                        active={active}
                      >
                        <Span fontVariant="label" color="blue font">
                          {labelMap[item]}
                        </Span>
                        <UnderlineHost>
                          <AnimatedLine className="animatedLine" />
                        </UnderlineHost>
                      </StyledBox>
                    </Flex>
                  )}
                </ListboxOption>
              ))}
            </ListboxOptions>
          )}
        </>
      )}
    </Listbox>
  );
}
